import { useMemo, useState } from 'react';

import type { PrivateEventDetail } from '../../api/privateEvents';

import { seedWhenWhat } from './private-event-edit-seed';
import type { PrivateEventFormFields } from './private-event-form-state';
import type { VenueOption } from './private-event-venues';
import { whenWhatErrors } from './private-event-when-what-errors';
import { movedEndDate, whenWhatPatch } from './private-event-when-what-patch';
import { usePrivateEventLocation } from './use-private-event-location';
import { useSectionDraft } from './use-section-draft';
import { usePrivateEventOptions } from './usePrivateEventOptions';

const LOCATION_KEYS: ReadonlyArray<keyof PrivateEventFormFields> = [
  'venueId',
  'locationName',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zipCode',
];

/** Error keys to drop when a field is edited, beyond its own. */
const RELATED_ERRORS: Partial<Record<keyof PrivateEventFormFields, string[]>> = {
  date: ['startsAt', 'endsAt'],
  startTime: ['startTime', 'endsAt'],
  endTime: ['endTime', 'endsAt'],
  endDate: ['endsAt'],
  state: ['state', 'timeZone'],
};

const LOCATION_ERROR_KEYS = ['regionId', 'venueId', 'timeZone', 'locationTbd'];

/** The event's own venue, kept in the list even when it is no longer an active
 *  one, so an unchanged location still shows and is never re-checked. */
function withCurrentVenue(
  options: VenueOption[],
  event: PrivateEventDetail,
  regionId: string,
): VenueOption[] {
  const missing = event.venueId && regionId === event.regionId;
  if (!missing || options.some((v) => v.id === event.venueId)) return options;
  const current = {
    id: event.venueId as string,
    name: event.venueName ?? 'Current venue',
    timeZone: event.timeZone,
  };
  return [current, ...options];
}

/**
 * The When & what form: the shared section draft plus the location control.
 * Seeded when editing starts, with the stored zone confirmed and no region
 * auto-filled. It sends a location only once the RD touched it and changed it.
 */
export function useWhenWhatForm(event: PrivateEventDetail, onDenied: () => void) {
  const options = usePrivateEventOptions();
  const [locationTouched, setLocationTouched] = useState(false);
  const section = useSectionDraft<PrivateEventFormFields>({
    event,
    seed: seedWhenWhat,
    patch: (seeded, draft) => whenWhatPatch(event, seeded, draft, locationTouched),
    validate: (_event, draft) => whenWhatErrors(draft),
    related: RELATED_ERRORS,
    onDenied,
  });
  const { clear, begin: beginSection } = section;
  const location = usePrivateEventLocation(section.draft, section.setDraft, options, {
    baseline: seedWhenWhat(event),
  });

  const touch = () => setLocationTouched(true);
  const venueOptions = useMemo(
    () => withCurrentVenue(location.venueOptions, event, section.draft.regionId),
    [location.venueOptions, event, section.draft.regionId],
  );

  return {
    ...options,
    fields: location.fields,
    withAddressLine2: true,
    venueOptions,
    fieldError: section.fieldError,
    set: <K extends keyof PrivateEventFormFields>(key: K, value: PrivateEventFormFields[K]) => {
      section.set(key, value);
      // The end date travels with the date, so a moved event is not checked
      // against the day it used to end on.
      if (key === 'date' && value) {
        section.setDraft((prev) => ({ ...prev, endDate: movedEndDate(section.seeded, prev) }));
      }
      if (LOCATION_KEYS.includes(key)) touch();
    },
    setRegion: (id: string) => {
      location.setRegion(id);
      clear(...LOCATION_ERROR_KEYS);
      touch();
    },
    setLocationMode: (mode: Parameters<typeof location.setLocationMode>[0]) => {
      location.setLocationMode(mode);
      clear(...LOCATION_ERROR_KEYS);
      touch();
    },
    setTimeZone: (zone: string) => {
      location.setTimeZone(zone);
      clear('timeZone');
      touch();
    },
    begin: () => {
      beginSection();
      setLocationTouched(false);
    },
    submit: () => section.submit(location.fields),
  };
}

export type WhenWhatForm = ReturnType<typeof useWhenWhatForm>;
